import { motion } from 'framer-motion';
import { ArrowRight, FileText, Copy, Check, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import ParticleBackground from './ParticleBackground';
import { SITE_CONFIG, getExchangeStatusLabel } from '@/config/site';
import { resolveBrandLogoUrl, BRAND_LOGO } from '@/lib/brandAssets';

const LOGO_ICON_URL = resolveBrandLogoUrl(SITE_CONFIG.heroLogoUrl);
const LOGO_FALLBACK = BRAND_LOGO;

export const Hero = () => {
  const [copied, setCopied] = useState(false);
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(SITE_CONFIG.contractAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] overflow-hidden bg-[#0a0b0d]"
      data-testid="hero-section"
    >
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Radial gradient overlays */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(156,121,65,0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_100%_100%,rgba(235,211,141,0.08),transparent)]" />
      
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(235,211,141,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(235,211,141,0.5) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-10 xl:px-16 min-h-[100svh] flex items-start lg:items-center">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center w-full pt-28 pb-10 sm:pt-32 sm:pb-12 lg:py-0">
          
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="order-2 lg:order-1"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-flex max-w-full items-center gap-2 bg-[#9C7941]/10 border border-[#9C7941]/30 px-4 py-2 rounded-full text-xs sm:text-sm text-[#EBD38D] font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EBD38D] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EBD38D]" />
                </span>
                Live on BNB Chain
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] mb-6"
              data-testid="hero-headline"
            >
              <span className="text-white block">The Gateway to</span>
              <span className="text-[#EBD38D] block">Next-Gen Trading</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-base sm:text-lg md:text-xl text-[#9A9B9F] max-w-xl mb-8 leading-relaxed"
              data-testid="hero-subheadline"
            >
              BITZX is a BNB Chain utility token building toward a broader trading
              ecosystem. Explore the token, review the contract, and follow official
              project updates as the platform develops.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <motion.a
                href={SITE_CONFIG.buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-full sm:w-auto overflow-hidden bg-gradient-to-r from-[#9C7941] to-[#EBD38D] text-[#0a0b0d] font-bold px-8 py-4 rounded-xl text-base flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(156,121,65,0.3)]"
                whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(156,121,65,0.5)' }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-buy-btn"
              >
                <span className="relative z-10">Buy $BZX</span>
                <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              </motion.a>

              <motion.button
                onClick={() => scrollToSection('#whitepaper')}
                className="group w-full sm:w-auto bg-transparent border-2 border-[#4A4B50] hover:border-[#EBD38D] text-[#D5D5D0] hover:text-[#EBD38D] font-semibold px-8 py-4 rounded-xl text-base flex items-center justify-center gap-2 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-whitepaper-btn"
              >
                <FileText size={18} />
                Read Whitepaper
              </motion.button>
            </motion.div>

            {/* Token Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              {[
                { label: 'Token', value: '$BZX', highlight: true },
                { label: 'Network', value: 'BNB Chain', highlight: false },
                { label: 'Supply', value: '1B', highlight: false },
                {
                  label: 'Exchange',
                  value: getExchangeStatusLabel(),
                  highlight: true,
                },
              ].map((stat) => (
                <div 
                  key={stat.label}
                  className="bg-[#12141a] border border-[#2a2d35] rounded-xl p-4 text-center hover:border-[#9C7941]/50 transition-colors"
                >
                  <p className="text-[#6B6D75] text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className={`font-bold text-sm ${stat.highlight ? 'text-[#EBD38D]' : 'text-white'}`}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Logo & Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="order-1 lg:order-2 relative flex items-center justify-center min-h-[280px] sm:min-h-[360px] lg:min-h-0"
          >
            {/* Glow rings behind logo */}
            <div className="absolute w-[220px] h-[220px] sm:w-[300px] sm:h-[300px] md:w-[400px] md:h-[400px] lg:w-[450px] lg:h-[450px] rounded-full border border-[#9C7941]/10" />
            <div className="absolute w-[190px] h-[190px] sm:w-[260px] sm:h-[260px] md:w-[340px] md:h-[340px] lg:w-[380px] lg:h-[380px] rounded-full border border-[#9C7941]/20" />
            <div className="absolute w-[160px] h-[160px] sm:w-[220px] sm:h-[220px] md:w-[280px] md:h-[280px] lg:w-[310px] lg:h-[310px] rounded-full bg-gradient-to-br from-[#9C7941]/10 to-transparent" />
            
            {/* Animated glow */}
            <motion.div
              animate={{
                boxShadow: [
                  '0 0 60px 30px rgba(156,121,65,0.1)',
                  '0 0 80px 40px rgba(156,121,65,0.2)',
                  '0 0 60px 30px rgba(156,121,65,0.1)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute w-[140px] h-[140px] sm:w-[180px] sm:h-[180px] md:w-[250px] md:h-[250px] lg:w-[280px] lg:h-[280px] rounded-full"
            />
            
            {/* Logo Image */}
            <motion.img
              src={LOGO_ICON_URL}
              alt="BITZX Token"
              className="relative z-10 w-[150px] h-[150px] sm:w-[200px] sm:h-[200px] md:w-[280px] md:h-[280px] lg:w-[320px] lg:h-[320px] object-contain drop-shadow-[0_0_50px_rgba(156,121,65,0.3)]"
              onError={(e) => {
                if (e.currentTarget.src === LOGO_FALLBACK) return;
                e.currentTarget.onerror = null;
                e.currentTarget.src = LOGO_FALLBACK;
              }}
              animate={{ 
                rotateY: [0, 5, 0, -5, 0],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              data-testid="hero-logo"
            />

            {/* Floating elements */}
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="hidden sm:block absolute top-5 right-5 md:top-10 md:right-10 lg:top-16 lg:right-0 bg-[#12141a]/90 backdrop-blur-sm border border-[#2a2d35] rounded-xl px-4 py-2.5 text-sm"
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[#6B6D75] text-xs">Token Standard</span>
              </div>
              <span className="text-[#EBD38D] font-bold">BEP-20</span>
            </motion.div>

            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="hidden sm:block absolute bottom-5 left-0 md:bottom-10 lg:bottom-16 bg-[#12141a]/90 backdrop-blur-sm border border-[#2a2d35] rounded-xl px-4 py-2.5 text-sm"
            >
              <span className="text-[#6B6D75] text-xs block mb-0.5">Whitepaper</span>
              <span className="text-white font-bold">Available</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Contract Address Bar */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="relative z-20 px-4 pb-8 sm:px-6 sm:pb-10 lg:absolute lg:bottom-6 lg:left-0 lg:right-0 lg:px-0 lg:pb-0"
      >
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#12141a]/90 backdrop-blur-md border border-[#2a2d35] rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3 min-w-0">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[#6B6D75] text-sm">Contract:</span>
              <code className="text-[#EBD38D] text-xs sm:text-sm font-mono break-all leading-relaxed">
                {SITE_CONFIG.contractAddress}
              </code>
            </div>
            <div className="flex items-center gap-3 self-end sm:self-auto">
              <button 
                onClick={handleCopy}
                className="text-[#6B6D75] hover:text-[#EBD38D] transition-colors p-2"
                data-testid="copy-contract-hero"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <a 
                href={SITE_CONFIG.bscScanUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#EBD38D] hover:text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                BscScan <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
